import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Form, InputNumber } from 'antd';
import { calculateLdm, calculateTotalLDM } from 'shared/utils/ldm.ts';
import { calculateVolume } from 'shared/utils/volume.ts';
import { defaultSizes } from 'components/common/cargo-item-input-fields/default-sizes';
import styles from './cargo-item-input-fields.module.scss';
import type { ICargoItemInputFieldsProps } from './types';
import { getCargoItemFieldsRule } from './validators';

export const CargoItemInputFields: React.FC<ICargoItemInputFieldsProps> = (props) => {
  const { form, name, index, withoutCount = false, withoutStack = false, isStackRequired = false } = props;
  const { t } = useTranslation('cargo');
  const [ldm, setLdm] = useState(0);
  const [volume, setVolume] = useState(0);

  const countFieldName = name === undefined || index === undefined ? 'count' : [name, index, 'count'];
  const lengthFieldName = name === undefined || index === undefined ? 'length' : [name, index, 'length'];
  const widthFieldName = name === undefined || index === undefined ? 'width' : [name, index, 'width'];
  const heightFieldName = name === undefined || index === undefined ? 'height' : [name, index, 'height'];
  const stackFieldName = name === undefined || index === undefined ? 'stack' : [name, index, 'stack'];
  const weightFieldName = name === undefined || index === undefined ? 'weight' : [name, index, 'weight'];

  const count = Form.useWatch(countFieldName, form);
  const length = Form.useWatch(lengthFieldName, form);
  const width = Form.useWatch(widthFieldName, form);
  const height = Form.useWatch(heightFieldName, form);
  const stack = Form.useWatch(stackFieldName, form);

  const totalLdm = calculateTotalLDM(count, stack ?? 1, ldm);

  const setDefaultSize = (length: number, width: number) => () => {
    form.setFieldValue(lengthFieldName, length);
    form.setFieldValue(widthFieldName, width);
  };

  const cargoItemFieldsRules = getCargoItemFieldsRule(isStackRequired);

  useEffect(() => {
    if (length && width) {
      setLdm(calculateLdm(length, width));
    } else {
      setLdm(0);
    }
    if (length && width && height) {
      setVolume(calculateVolume(length, width, height));
    } else {
      setVolume(0);
    }
  }, [length, width, height]);

  return (
    <div>
      <div className={styles.container}>
        {!withoutCount ? (
          <Form.Item
            name={countFieldName}
            rules={cargoItemFieldsRules.count}
            label={t('count_label')}
            rootClassName={styles.inputBox}
            hasFeedback={false}>
            <InputNumber min={1} step={1} precision={0} />
          </Form.Item>
        ) : null}

        <Form.Item
          name={lengthFieldName}
          rules={cargoItemFieldsRules.length}
          label={t('length_label')}
          rootClassName={styles.inputBox}>
          <InputNumber decimalSeparator={','} controls={false} min={0} />
        </Form.Item>
        <Form.Item
          name={widthFieldName}
          rules={cargoItemFieldsRules.width}
          label={t('width_label')}
          rootClassName={styles.inputBox}>
          <InputNumber decimalSeparator={','} controls={false} min={0} />
        </Form.Item>
        <Form.Item
          name={heightFieldName}
          rules={cargoItemFieldsRules.height}
          label={t('height_label')}
          rootClassName={styles.inputBox}>
          <InputNumber decimalSeparator={','} controls={false} min={0} />
        </Form.Item>
        {!withoutStack ? (
          <Form.Item
            name={stackFieldName}
            rules={cargoItemFieldsRules.stack}
            label={t('stacks_label')}
            rootClassName={styles.inputBox}>
            <InputNumber min={1} step={1} precision={0} />
          </Form.Item>
        ) : null}
        <Form.Item
          name={weightFieldName}
          rules={cargoItemFieldsRules.weight}
          label={t('weight_label')}
          rootClassName={styles.inputBox}>
          <InputNumber decimalSeparator={','} controls={false} min={0} />
        </Form.Item>
        <Form.Item label={t('volume_label')} rootClassName={styles.inputBox}>
          <InputNumber controls={false} value={volume} decimalSeparator={','} disabled />
        </Form.Item>
        <Form.Item label={t('ldm_label')} rootClassName={styles.inputBox}>
          <InputNumber controls={false} value={ldm} decimalSeparator={','} disabled />
        </Form.Item>
      </div>
      <Flex justify='space-between'>
        <Flex gap='small' className={styles.defaultSizes}>
          {defaultSizes.map((size) => (
            <span key={size.title} onClick={setDefaultSize(size.length, size.width)}>
              {size.title}
            </span>
          ))}
        </Flex>
        {!withoutCount ? (
          <div className={styles.containerTotal}>
            <strong>Total</strong>: LDM = {totalLdm} m, Volume = {volume * count || 0} m3
          </div>
        ) : null}
      </Flex>
    </div>
  );
};
